import { CODE128_QUIET_ZONE_MODULES, encodeCode128 } from '../../barcode/code128.ts';

export interface BarcodeProps {
  /** Printable ASCII, e.g. a sample number. */
  value: string;
  /**
   * Width of one module in millimetres. 0.25 mm is two dots on a 203 dpi thermal printer
   * and three on a 300 dpi one, so every bar lands on whole dots (lab plan §12, risk 4).
   */
  moduleMm?: number;
  heightMm?: number;
}

/**
 * A Code 128 symbol as SVG (lab plan §5.9). The viewBox is one unit per module, so every
 * bar starts and ends on a whole module; the physical size is set in millimetres for
 * print. The value is printed underneath by the caller (BarcodeArea), for when a scan
 * fails.
 */
export function Barcode({ value, moduleMm = 0.25, heightMm = 10 }: BarcodeProps) {
  const symbol = encodeCode128(value);
  if (!symbol.ok) return <span>This number can't be printed as a barcode.</span>;
  const total = symbol.value.modules + 2 * CODE128_QUIET_ZONE_MODULES;
  const bars: { x: number; width: number }[] = [];
  let x = CODE128_QUIET_ZONE_MODULES;
  symbol.value.widths.forEach((width, index) => {
    if (index % 2 === 0) bars.push({ x, width });
    x += width;
  });
  return (
    <svg
      role="img"
      aria-label={`Barcode ${value}`}
      viewBox={`0 0 ${String(total)} 1`}
      width={`${String(total * moduleMm)}mm`}
      height={`${String(heightMm)}mm`}
      preserveAspectRatio="none"
      shapeRendering="crispEdges"
    >
      {bars.map((bar) => (
        <rect key={bar.x} x={bar.x} y={0} width={bar.width} height={1} fill="var(--ink)" />
      ))}
    </svg>
  );
}
